class ClassickShopMegaMenu extends HTMLElement {
  #controller;
  #resizeObserver;
  #frame;
  #focusTimer;

  connectedCallback() {
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    clearTimeout(this.#focusTimer);
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    const headerMenu = this.closest('header-menu');
    const trigger = this.closest('.menu-list__submenu')?.parentElement?.querySelector('[ref="menuitem"]');

    trigger?.addEventListener('keydown', (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      const enterWithTab = event.key === 'Tab' && !event.shiftKey && trigger.getAttribute('aria-expanded') === 'true';
      if (!enterWithTab && event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      if (typeof headerMenu?.activate !== 'function') return;
      event.preventDefault();
      headerMenu.activate(event);
      clearTimeout(this.#focusTimer);
      this.#focusTimer = setTimeout(() => {
        if (!this.isConnected || document.activeElement !== trigger || trigger.getAttribute('aria-expanded') !== 'true') return;
        const categories = this.querySelectorAll('[data-shop-category]');
        const category = categories[event.key === 'ArrowUp' ? categories.length - 1 : 0];
        category?.focus({ preventScroll: true });
      }, 0);
    }, options);

    this.addEventListener('focusin', (event) => {
      if (typeof headerMenu?.activate === 'function') headerMenu.activate(event);
    }, options);

    this.querySelectorAll('[data-shop-category]').forEach((link) => {
      link.removeAttribute('aria-expanded');
      link.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'touch') this.#activate(link.dataset.shopCategory);
      }, options);
      link.addEventListener('focus', () => this.#activate(link.dataset.shopCategory), options);
      link.addEventListener('keydown', (event) => {
        if (['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
          const categories = [...this.querySelectorAll('[data-shop-category]')];
          const index = categories.indexOf(link);
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? categories.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + categories.length) % categories.length;
          event.preventDefault();
          categories[next]?.focus();
          return;
        }
        if (event.key !== 'ArrowRight') return;
        const panel = this.querySelector('[data-shop-panel]:not([hidden])');
        const firstLink = panel?.querySelector('.ck-shop__card, .ck-shop__campaign');
        if (firstLink) {
          event.preventDefault();
          firstLink.focus();
        }
      }, options);
    });

    this.querySelectorAll('[data-shop-panel]').forEach((panel) => {
      panel.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft') return;
        const category = this.querySelector('.ck-shop__category.is-active');
        if (category) {
          event.preventDefault();
          category.focus();
        }
      }, options);
    });

    this.#resizeObserver = new ResizeObserver(() => this.#syncHeight());
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#focusTimer);
  }

  #activate(handle) {
    if (!handle) return;
    this.querySelectorAll('[data-shop-category]').forEach((link) => {
      const active = link.dataset.shopCategory === handle;
      link.classList.toggle('is-active', active);
    });
    this.querySelectorAll('[data-shop-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.shopPanel !== handle;
    });
    this.#syncHeight();
  }

  #syncHeight() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const submenu = this.closest('.menu-list__submenu');
      const headerMenu = this.closest('header-menu');
      const trigger = submenu?.parentElement?.querySelector('[ref="menuitem"]');
      if (submenu && headerMenu && trigger?.getAttribute('aria-expanded') === 'true') {
        headerMenu.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
      }
    });
  }
}

if (!customElements.get('ck-shop-mega-menu')) {
  customElements.define('ck-shop-mega-menu', ClassickShopMegaMenu);
}

class ClassickMobileShopMenu extends HTMLElement {
  #controller;
  #activeTrigger;
  #consumeEscape = false;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    const drawer = this.closest('header-drawer');
    const details = drawer?.querySelector('details');

    this.querySelectorAll('[data-mobile-shop-category]').forEach((link) => {
      link.setAttribute('role', 'button');
      link.setAttribute('aria-expanded', 'false');
      link.addEventListener('click', (event) => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.#open(link);
      }, options);
      link.addEventListener('keydown', (event) => {
        if (event.key !== ' ') return;
        event.preventDefault();
        this.#open(link);
      }, options);
    });

    this.querySelectorAll('[data-mobile-shop-back]').forEach((button) => {
      button.addEventListener('click', () => this.#back(true), options);
    });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.#activeTrigger) return;
      event.preventDefault();
      event.stopPropagation();
      this.#consumeEscape = true;
      this.#back(true);
    }, options);
    this.addEventListener('keyup', (event) => {
      if (event.key !== 'Escape' || !this.#consumeEscape) return;
      event.stopPropagation();
      this.#consumeEscape = false;
    }, options);

    details?.addEventListener('toggle', () => {
      if (!details.open) this.#back(false);
    }, options);

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || !details?.open || !drawer) return;
      const surface = drawer.querySelector('[data-header-drawer]');
      if (!surface) return;
      const focusable = [...surface.querySelectorAll('a[href], button:enabled, summary, input:enabled, select:enabled, [tabindex="0"]')]
        .filter((element) => !element.closest('[hidden], [inert]') && element.getClientRects().length);
      const first = focusable[0];
      const last = focusable.at(-1);
      if ((!event.shiftKey && event.target === last) || (event.shiftKey && event.target === first)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        (event.shiftKey ? last : first)?.focus();
      }
    }, { ...options, capture: true });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #open(link) {
    const panel = [...this.querySelectorAll('[data-mobile-shop-panel]')]
      .find((item) => item.dataset.mobileShopPanel === link.dataset.mobileShopCategory);
    if (!panel) return;
    this.#activeTrigger = link;
    link.setAttribute('aria-expanded', 'true');
    this.querySelector('[data-mobile-shop-root]').hidden = true;
    this.querySelectorAll('[data-mobile-shop-panel]').forEach((item) => { item.hidden = item !== panel; });
    this.dataset.mobileShopView = 'category';
    this.closest('[data-header-drawer]')?.scrollTo({ top: 0, behavior: 'instant' });
    panel.querySelector('[data-mobile-shop-back]')?.focus({ preventScroll: true });
  }

  #back(restoreFocus) {
    const trigger = this.#activeTrigger;
    this.querySelector('[data-mobile-shop-root]').hidden = false;
    this.querySelectorAll('[data-mobile-shop-panel]').forEach((panel) => { panel.hidden = true; });
    this.querySelectorAll('[data-mobile-shop-category]').forEach((link) => link.setAttribute('aria-expanded', 'false'));
    delete this.dataset.mobileShopView;
    this.#activeTrigger = null;
    if (restoreFocus) {
      this.closest('[data-header-drawer]')?.scrollTo({ top: 0, behavior: 'instant' });
      trigger?.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('ck-mobile-shop-menu')) {
  customElements.define('ck-mobile-shop-menu', ClassickMobileShopMenu);
}
